import React from 'react';
import * as enums from '@local/finsera-core/src/enums';
import { argsToStringifiable } from '@local/finsera-core/src/helpers/embed/utils';
import { _, moment, mui } from '@local/finsera-core/src/libs';
import * as types from '@local/finsera-core/src/types';

import Chart from './chart';
import { getPeriodData } from './methods/get-period-data';
import PeriodSelector from './period-selector';
import { useUiStyles, useWindowDimensions } from '../../../hooks';
import CenteredLoader from '../../centered-loader';
import ChartError from '../../chart-error';

type LineChartProps = {
  id: string;
  data: types.widgets.TableData;
  fullScreen: boolean;
  readOnly: boolean;

  alert?: {
    type: 'error' | 'warning';
    message: string;
  };
  showPeriodSelector?: boolean;
  xAxisField?: string;
  numberFormat?: string;
  showEndValueLabel?: boolean;
  exportTitle?: string;
  exportSubTitle?: string;
  download?: boolean;
  setExternalEmbed?: (_json: types.analysis.EmbedChartData) => void;
  yAxisLabel?: string;
  xAxisLabel?: string;
  customDownloadFormatting?: (_data: types.widgets.TableData) => types.widgets.TableData;
  withoutSeries?: string[];
  withoutValuePadding?: boolean;
  hideLegend?: boolean;
  title?: string;
  dotted?: string[];
  periodsForceSameStart?: boolean;
  withGradient?: boolean;
  seriesColorByName?: Record<string, string>;
  colorizeBySign?: boolean;
};

/**
 * Line chart component that displays data in a line format
 *
 * @remarks
 * If xAxisField is set to 'date' (default), the values will be treated as dates
 * and the chart will behave as a time series chart. This enables additional features
 * like period selection.
 *
 * @param id - Unique identifier for the chart
 * @param data - Data to be displayed in the chart
 * @param fullScreen - Whether the chart is in fullscreen mode
 * @param readOnly - Whether the chart is in read-only mode
 * @param alert - Alert message to display above the chart
 * @param showPeriodSelector - Whether to show period selector (only works when xAxisField is 'date')
 * @param xAxisField - Field to use for x-axis values. Default is 'date' for time series
 * @param numberFormat - Format for numbers on the y-axis
 * @param showEndValueLabel - Whether to show value labels at the end of lines
 * @param exportTitle - Title used when exporting the chart
 * @param exportSubTitle - Subtitle used when exporting the chart
 * @param download - Whether to show download button
 * @param setExternalEmbed - Callback to set external embed data
 * @param yAxisLabel - Label for y-axis
 * @param xAxisLabel - Label for x-axis
 * @param periodsForceSameStart - Whether to force the same start date for all periods
 * @param withGradient - Whether to show gradient
 * @param seriesColorByName - Color by name
 * @param colorizeBySign - Whether to colorize by positive or negative values
 * (only works when showPeriodSelector is true)
 * @returns React component displaying a line chart
 */
const LineChart = (args: LineChartProps) => {
  const {
    id,
    data,
    fullScreen,
    readOnly,
    alert,
    showPeriodSelector = false,
    xAxisField = 'date',
    numberFormat = '#.##',
    showEndValueLabel = false,
    exportTitle,
    exportSubTitle,
    download = true,
    setExternalEmbed,
    yAxisLabel,
    xAxisLabel,
    customDownloadFormatting = undefined,
    withoutSeries = undefined,
    withoutValuePadding = false,
    hideLegend = false,
    title,
    dotted,
    periodsForceSameStart = false,
    withGradient = false,
    seriesColorByName,
    colorizeBySign = false,
  } = args;

  const uiStyles = useUiStyles();
  const [localData, setLocalData] = React.useState<types.widgets.TableData>(null);
  const [selectedPeriod, setSelectedPeriod] = React.useState<enums.PERIOD_ENUM>();
  const { width } = useWindowDimensions(1_000);

  const { allData, availablePeriods } = React.useMemo(() => {
    if (_.isEmpty(data))
      return { allData: null as Record<enums.PERIOD_ENUM, types.widgets.TableData>, availablePeriods: [] };
    return getPeriodData({ data, xAxisField, forceSameStart: periodsForceSameStart });
  }, [data]);

  React.useEffect(() => {
    if (periodsForceSameStart && availablePeriods.length > 0) setSelectedPeriod(availablePeriods.at(-1));
    else setSelectedPeriod(enums.PERIOD_ENUM.FULL);
  }, [availablePeriods]);

  const isDate = xAxisField.includes('date');

  React.useEffect(() => {
    const handleData = async () => {
      setLocalData(null);
      if (_.isNil(data)) return;
      await new Promise((resolve) => setTimeout(resolve, 200));
      if (setExternalEmbed)
        setExternalEmbed({
          id,
          widgetTitle: exportTitle,
          widgetSubTitle: exportSubTitle,
          chart_type: 'line-chart-v5',
          args: argsToStringifiable({ ...args, data }),
        });
      const seriesData = allData?.[selectedPeriod] ?? [];
      const readyData = showPeriodSelector ? seriesData : data;
      if (isDate) {
        setLocalData(
          readyData
            .map((row) => ({ ...row, date: moment(row[xAxisField] as string).valueOf() as number }))
            .filter((row) => !!row.date)
            .sort((a, b) => a.date - b.date)
        );
      } else {
        setLocalData(readyData);
      }
    };
    handleData();
  }, [allData, selectedPeriod, width]);

  return (
    <mui.core.Box
      sx={{
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        display: 'grid',
        gridTemplateRows: `${alert ? 'auto ' : ''} ${showPeriodSelector ? 'auto ' : ''} 1fr`,
      }}
    >
      {alert && <ChartError readOnly={readOnly} error={alert.message} severity={alert.type} />}
      {showPeriodSelector && isDate && (
        <PeriodSelector
          periods={availablePeriods}
          selectedPeriod={selectedPeriod}
          setSelectedPeriod={setSelectedPeriod}
        />
      )}
      <mui.core.Box sx={uiStyles.widgetContent}>
        <mui.core.Box sx={{ height: fullScreen ? 'calc(100% - 40px)' : '100%' }}>
          {_.isNil(localData) ? (
            <CenteredLoader label="Loading widget data..." top="-40px" />
          ) : (
            <Chart
              id={id}
              data={localData}
              fullScreen={fullScreen}
              xAxisField={xAxisField}
              numberFormat={numberFormat}
              showEndValueLabel={showEndValueLabel}
              download={download}
              yAxisLabel={yAxisLabel}
              xAxisLabel={xAxisLabel}
              customDownloadFormatting={customDownloadFormatting}
              withoutSeries={withoutSeries}
              withoutValuePadding={withoutValuePadding}
              hideLegend={hideLegend}
              title={title}
              dotted={dotted}
              withGradient={withGradient}
              seriesColorByName={seriesColorByName}
              colorizeBySign={colorizeBySign}
            />
          )}
        </mui.core.Box>
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default LineChart;
